import React, { useState } from 'react';
import { formRowSx } from '../utils/panelSurfaces';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl
} from '@mui/material';

// The Add-value box holds a multi-digit ordinal (a nakshatra runs to 27, and Group 1's rows
// are seeded with values a user then edits). At 56px a three-digit entry was clipped by the
// number spinner, so the figure being submitted could not be read back. 84 = 56 x 1.5.
const ADD_VALUE_WIDTH = 84;

const ALL_PLANETS     = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const DAY_LORD_PLANETS = ['Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus'];
const STATES          = ['Direct', 'Retro', 'Combust', 'Rising', 'Station', 'Average', 'Slow', 'Fast', 'Superfast', 'Accelerating', 'Decelerating'];
const DAYS            = Array.from({ length: 31 }, (_, i) => String(i + 1));
const MONTHS          = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PAKSHAS         = ['Shukla', 'Krishna'];
const TITHIS          = ['Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami', 'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami', 'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima/Amavasya'];
const WEEKDAYS        = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const YOGS            = ['Vishkumbha', 'Preeti', 'Ayushman', 'Saubhagya', 'Shobhana', 'Atiganda', 'Sukarma', 'Dhriti', 'Shoola', 'Ganda', 'Vriddhi', 'Dhruva', 'Vyaghata', 'Harshana', 'Vajra', 'Siddhi', 'Vyatipata', 'Variyan', 'Parigha', 'Shiva', 'Siddha', 'Sadhya', 'Shubha', 'Shukla', 'Brahma', 'Mahendra', 'Vaidhriti'];
const VEDIC_MONTHS    = ['Chaitra', 'Vaishakha', 'Jyeshtha', 'Ashadha', 'Shravana', 'Bhadrapada', 'Ashvina', 'Kartika', 'Margashirsha', 'Pausha', 'Magha', 'Phalguna'];
const SIGNS           = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

const WIN_DEFAULTS = { nDays: '7', windowMode: 'before' };

const initForms = {
  r1: { planet: 'Sun',    state: 'Average',      value: '2' },
  r2: { day: '1',         ...WIN_DEFAULTS, value: '2' },
  r3: { month: 'January',                         value: '2' },
  r4: { paksha: 'Shukla', tithi: 'Pratipada', ...WIN_DEFAULTS, value: '2' },
  r5: { weekday: 'Monday', ...WIN_DEFAULTS, value: '2' },
  r9: { yog: 'Vishkumbha', ...WIN_DEFAULTS, value: '2' },
  r6: { vedicMonth: 'Chaitra',                    value: '2' },
  r7: { dayLord: 'Moon', planet: 'Mercury', aspectDeg: '2', ...WIN_DEFAULTS, value: '2' },
  r8: { dayLord: 'Moon', sign: 'Aries', ...WIN_DEFAULTS, value: '2' },
};

const mkSel = (items, minW) => {
  const C = ({ value, onChange }) => (
    <FormControl size="small" sx={{ minWidth: minW }}>
      <Select value={value} onChange={e => onChange(e.target.value)}>
        {items.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
      </Select>
    </FormControl>
  );
  C.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };
  return C;
};

const PlanetSelect    = mkSel(ALL_PLANETS, 110);
const DayLordSelect   = mkSel(DAY_LORD_PLANETS, 110);
const SUN_EXCLUDED_STATES = ['Direct', 'Retro', 'Combust', 'Rising', 'Station'];
const StateSelect = ({ value, onChange, exclude }) => (
  <FormControl size="small" sx={{ minWidth: 140 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {STATES.filter(s => !exclude || !exclude.includes(s)).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
    </Select>
  </FormControl>
);
StateSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, exclude: PropTypes.array };
const DaySelect       = mkSel(DAYS, 70);
const MonthSelect     = mkSel(MONTHS, 130);
const PakshaSelect    = mkSel(PAKSHAS, 110);
const TithiSelect     = mkSel(TITHIS, 170);
const WeekdaySelect   = mkSel(WEEKDAYS, 120);
const YogSelect       = mkSel(YOGS, 150);
const VedicMonthSelect = mkSel(VEDIC_MONTHS, 140);
const SignSelect      = mkSel(SIGNS, 130);

const Label = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{children}</Typography>
);
Label.propTypes = { children: PropTypes.node.isRequired };

const WINDOW_MODE_OPTIONS = [
  { value: 'before', label: 'From specified number of days before the event to the event' },
  { value: 'after',  label: 'From the event to the specified number of days after' },
  { value: 'around', label: 'From specified number of days before the event to the event to after the event' },
];

/**
 * The window picker, and the one control on a row that is allowed to give way.
 *
 * Its options are whole sentences, so at a fixed 420px it was the widest thing in the row and the
 * reason the longest row could not fit its ADD button on one line. It now flexes: it takes the
 * space a row has spare and shrinks — down to 240px, still enough to read the start of an option
 * and tell the three apart — before anything else on the row is allowed to move.
 */
const EventWindowControls = ({ nDays, windowMode, onNDaysChange, onModeChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flex: '1 1 auto', minWidth: 0 }}>
    <TextField size="small" type="number" value={nDays} onChange={e => onNDaysChange(e.target.value)}
      inputProps={{ min: 1, step: 1 }} sx={{ width: 64, flexShrink: 0 }} />
    <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>days</Typography>
    <FormControl size="small" sx={{ flex: '1 1 420px', minWidth: 240 }}>
      <Select value={windowMode} onChange={e => onModeChange(e.target.value)}>
        {WINDOW_MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  </Box>
);
EventWindowControls.propTypes = {
  nDays: PropTypes.string.isRequired, windowMode: PropTypes.string.isRequired,
  onNDaysChange: PropTypes.func.isRequired, onModeChange: PropTypes.func.isRequired,
};

const NumField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)} sx={{ width: 80 }} />
);
NumField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddValueField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)}
    inputProps={{ min: 1, step: 1 }} sx={{ width: ADD_VALUE_WIDTH }} />
);
AddValueField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddBtn = ({ onClick, disabled }) => (
  <Button variant="contained" size="small" onClick={onClick} disabled={disabled}
    sx={{ minWidth: 56, px: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
    ADD
  </Button>
);
AddBtn.propTypes = { onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

/**
 * The "Add value / n / ADD" tail every row ends with, as one indivisible unit.
 *
 * Each row used to build this out of three loose children preceded by a flex spacer. Loose
 * children are wrap candidates in their own right, so on the longest row the button broke to a
 * second line on its own and that row's ADD no longer lined up with the others'. Grouped, the
 * three either sit together or wrap together, and `flexShrink: 0` means they are never what gives
 * way — the window select is. `ml: auto` does the job the spacer did, without the extra node.
 */
const AddCluster = ({ value, onChange, onAdd }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexShrink: 0 }}>
    <Label>Add value</Label>
    <AddValueField value={value} onChange={onChange} />
    <AddBtn onClick={onAdd} />
  </Box>
);
AddCluster.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

const CriteriaRow = ({ children }) => (
  <Box sx={(theme) => ({
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
    borderRadius: 1, p: 1, mb: 1, ...formRowSx('tinted')(theme),
    // Every row is the same height whether or not it carries a window picker, so the ADD buttons
    // form a straight column down the tab instead of stepping in and out with the row contents.
    minHeight: 56
  })}>
    {children}
  </Box>
);
CriteriaRow.propTypes = { children: PropTypes.node.isRequired };

const MOAGroup5Tab = ({ onAddCondition, forms: propForms, onFormsChange }) => {
  const [localForms, setLocalForms] = useState(initForms);
  const forms = propForms || localForms;
  const setForms = onFormsChange || setLocalForms;
  const set = (row, field, val) => setForms(f => ({ ...f, [row]: { ...f[row], [field]: val } }));

  const add = (type, condObj) => {
    const val = parseInt(condObj.value, 10);
    if (!val || val < 1) return;
    onAddCondition({ group: 5, type, ...condObj, value: val });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mb: 2, letterSpacing: 0.5 }}>
        Esoterica++ Criteria
      </Typography>

      {/* Row 1: Planet State */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r1.planet} onChange={v => {
          set('r1', 'planet', v);
          if (v === 'Sun' && SUN_EXCLUDED_STATES.includes(forms.r1.state)) set('r1', 'state', 'Average');
        }} />
        <Label>State</Label>
        <StateSelect value={forms.r1.state} onChange={v => set('r1', 'state', v)} exclude={forms.r1.planet === 'Sun' ? SUN_EXCLUDED_STATES : []} />
        <AddCluster value={forms.r1.value} onChange={v => set('r1', 'value', v)}
          onAdd={() => add('g5_planet_state', { planet: forms.r1.planet, state: forms.r1.state, value: forms.r1.value })} />
      </CriteriaRow>

      {/* Row 2: Day is */}
      <CriteriaRow>
        <Label>Day is</Label>
        <DaySelect value={forms.r2.day} onChange={v => set('r2', 'day', v)} />
        <EventWindowControls nDays={forms.r2.nDays} windowMode={forms.r2.windowMode}
          onNDaysChange={v => set('r2', 'nDays', v)} onModeChange={v => set('r2', 'windowMode', v)} />
        <AddCluster value={forms.r2.value} onChange={v => set('r2', 'value', v)}
          onAdd={() => add('g5_day_is', { day: Number(forms.r2.day), nDays: Number(forms.r2.nDays), windowMode: forms.r2.windowMode, value: forms.r2.value })} />
      </CriteriaRow>

      {/* Row 3: Month is */}
      <CriteriaRow>
        <Label>Month is</Label>
        <MonthSelect value={forms.r3.month} onChange={v => set('r3', 'month', v)} />
        <AddCluster value={forms.r3.value} onChange={v => set('r3', 'value', v)}
          onAdd={() => add('g5_month_is', { month: forms.r3.month, value: forms.r3.value })} />
      </CriteriaRow>

      {/* Row 4: Day Paksha And Day Number (Tithi) */}
      <CriteriaRow>
        <Label>Day Paksha</Label>
        <PakshaSelect value={forms.r4.paksha} onChange={v => set('r4', 'paksha', v)} />
        <Label>And Day Number</Label>
        <TithiSelect value={forms.r4.tithi} onChange={v => set('r4', 'tithi', v)} />
        <EventWindowControls nDays={forms.r4.nDays} windowMode={forms.r4.windowMode}
          onNDaysChange={v => set('r4', 'nDays', v)} onModeChange={v => set('r4', 'windowMode', v)} />
        <AddCluster value={forms.r4.value} onChange={v => set('r4', 'value', v)}
          onAdd={() => add('g5_paksha_tithi', { paksha: forms.r4.paksha, tithi: forms.r4.tithi, nDays: Number(forms.r4.nDays), windowMode: forms.r4.windowMode, value: forms.r4.value })} />
      </CriteriaRow>

      {/* Row 5: Weekday */}
      <CriteriaRow>
        <Label>Weekday</Label>
        <WeekdaySelect value={forms.r5.weekday} onChange={v => set('r5', 'weekday', v)} />
        <EventWindowControls nDays={forms.r5.nDays} windowMode={forms.r5.windowMode}
          onNDaysChange={v => set('r5', 'nDays', v)} onModeChange={v => set('r5', 'windowMode', v)} />
        <AddCluster value={forms.r5.value} onChange={v => set('r5', 'value', v)}
          onAdd={() => add('g5_weekday', { weekday: forms.r5.weekday, nDays: Number(forms.r5.nDays), windowMode: forms.r5.windowMode, value: forms.r5.value })} />
      </CriteriaRow>

      {/* Row 9: Yog */}
      <CriteriaRow>
        <Label>Yog</Label>
        <YogSelect value={forms.r9.yog} onChange={v => set('r9', 'yog', v)} />
        <EventWindowControls nDays={forms.r9.nDays} windowMode={forms.r9.windowMode}
          onNDaysChange={v => set('r9', 'nDays', v)} onModeChange={v => set('r9', 'windowMode', v)} />
        <AddCluster value={forms.r9.value} onChange={v => set('r9', 'value', v)}
          onAdd={() => add('g5_yog', { yog: forms.r9.yog, nDays: Number(forms.r9.nDays), windowMode: forms.r9.windowMode, value: forms.r9.value })} />
      </CriteriaRow>

      {/* Row 6: Vedic Month */}
      <CriteriaRow>
        <Label>Vedic Month</Label>
        <VedicMonthSelect value={forms.r6.vedicMonth} onChange={v => set('r6', 'vedicMonth', v)} />
        <AddCluster value={forms.r6.value} onChange={v => set('r6', 'value', v)}
          onAdd={() => add('g5_vedic_month', { vedicMonth: forms.r6.vedicMonth, value: forms.r6.value })} />
      </CriteriaRow>

      {/* Row 7: Day Lord of Planet And Planet At Aspect Degrees */}
      <CriteriaRow>
        <Label>Day Lord is Planet</Label>
        <DayLordSelect value={forms.r7.dayLord} onChange={v => set('r7', 'dayLord', v)} />
        <Label>And Planet</Label>
        <PlanetSelect value={forms.r7.planet} onChange={v => set('r7', 'planet', v)} />
        <Label>At Aspect Degrees</Label>
        <NumField value={forms.r7.aspectDeg} onChange={v => set('r7', 'aspectDeg', v)} />
        <EventWindowControls nDays={forms.r7.nDays} windowMode={forms.r7.windowMode}
          onNDaysChange={v => set('r7', 'nDays', v)} onModeChange={v => set('r7', 'windowMode', v)} />
        <AddCluster value={forms.r7.value} onChange={v => set('r7', 'value', v)}
          onAdd={() => add('g5_day_lord_aspect', { dayLord: forms.r7.dayLord, planet: forms.r7.planet, aspectDeg: Number(forms.r7.aspectDeg), nDays: Number(forms.r7.nDays), windowMode: forms.r7.windowMode, value: forms.r7.value })} />
      </CriteriaRow>

      {/* Row 8: Day Lord of Planet Ingress in Rashi */}
      <CriteriaRow>
        <Label>Day Lord is Planet</Label>
        <DayLordSelect value={forms.r8.dayLord} onChange={v => set('r8', 'dayLord', v)} />
        <Label>Ingress in Rashi</Label>
        <SignSelect value={forms.r8.sign} onChange={v => set('r8', 'sign', v)} />
        <EventWindowControls nDays={forms.r8.nDays} windowMode={forms.r8.windowMode}
          onNDaysChange={v => set('r8', 'nDays', v)} onModeChange={v => set('r8', 'windowMode', v)} />
        <AddCluster value={forms.r8.value} onChange={v => set('r8', 'value', v)}
          onAdd={() => add('g5_day_lord_ingress', { dayLord: forms.r8.dayLord, sign: forms.r8.sign, nDays: Number(forms.r8.nDays), windowMode: forms.r8.windowMode, value: forms.r8.value })} />
      </CriteriaRow>

    </Box>
  );
};

MOAGroup5Tab.propTypes = {
  onAddCondition: PropTypes.func.isRequired,
  forms: PropTypes.object,
  onFormsChange: PropTypes.func,
};

export default MOAGroup5Tab;
